import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableBody, TableRow, TableCell } from '@mui/material';
import { Edit, Delete as DeleteIcon, LockReset, Block, CheckCircle, InfoOutlined } from '@mui/icons-material';
import Progress from '../Progress';
import Notification from '../Notification';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_API_BASE_URL}/company-master/users`;

const maskText = (text) => {
  if (!text) return '';
  if (text.length <= 2) return '*'.repeat(text.length);
  return `${text[0]}${'*'.repeat(text.length - 2)}${text.slice(-1)}`;
};

const maskEmail = (email) => {
  if (!email) return '';
  const [username, domain] = email.split('@');
  return `${maskText(username)}@${domain}`;
};

const formatDate = (date) => (date ? new Date(date).toLocaleDateString('en-IN') : '-');

function UsersTable({ onEdit, onResetPassword, reloadKey }) {
  const [users, setUsers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [infoUser, setInfoUser] = useState(null);
  const navigate = useNavigate();

  const handleApiError = (e, fallbackMessage) => {
    if (e.response && e.response.status === 401) {
      setNotify({ isOpen: true, message: 'Session Expired!', type: 'error' });
      setTimeout(() => navigate('/'), 1000);
    } else {
      setNotify({ isOpen: true, message: e.response?.data?.message || e.response?.data?.error_message || fallbackMessage, type: 'error' });
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API);
      setUsers(response.data?.data || []);
      setCurrentUserId(response.data?.currentUserId ?? null);
    } catch (e) {
      handleApiError(e, 'Failed to fetch users');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const handleToggleStatus = async (user) => {
    const activate = Number(user.is_active) !== 1;
    const confirmed = window.confirm(
      activate
        ? `Activate user "${user.username}"? They will be able to log in again.`
        : `Deactivate user "${user.username}"? They will not be able to log in until activated again.`
    );
    if (!confirmed) return;

    try {
      const response = await axios.post(`${API}/${user.user_id}/status`, { is_active: activate ? 1 : 0 });
      setNotify({ isOpen: true, message: response.data.message, type: 'success' });
      fetchUsers();
    } catch (e) {
      handleApiError(e, 'Failed to update user status');
    }
  };

  const handleDelete = async (user) => {
    const confirmed = window.confirm(
      `Permanently delete user "${user.username}"? This cannot be undone. Consider deactivating instead.`
    );
    if (!confirmed) return;

    try {
      const response = await axios.delete(`${API}/${user.user_id}`);
      setNotify({ isOpen: true, message: response.data.message, type: 'success' });
      fetchUsers();
    } catch (e) {
      handleApiError(e, 'Failed to delete user');
    }
  };

  if (loading) {
    return <Progress />;
  }

  return (
    <>
      <div className="table-parent">
        <table className="table big-table">
          <thead className="table-dark">
            <tr>
              <th scope="col" className="table-dark">Actions</th>
              <th scope="col">Name</th>
              <th scope="col">Username</th>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Access Role</th>
              <th scope="col">Status</th>
              <th scope="col">Created On</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center' }}>No users found</td>
              </tr>
            )}
            {users.map((user) => {
              const isSelf = String(user.user_id) === String(currentUserId);
              const isActive = Number(user.is_active) === 1;
              return (
                <tr key={user.user_id}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="View Details">
                      <IconButton size="small" color="info" onClick={() => setInfoUser(user)}>
                        <InfoOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Modify">
                      <IconButton size="small" color="primary" onClick={() => onEdit(user)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset Password">
                      <IconButton size="small" color="secondary" onClick={() => onResetPassword(user)}>
                        <LockReset fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isSelf ? 'You cannot deactivate your own account' : (isActive ? 'Deactivate' : 'Activate')}>
                      <span>
                        <IconButton size="small" color={isActive ? 'warning' : 'success'} disabled={isSelf} onClick={() => handleToggleStatus(user)}>
                          {isActive ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={isSelf ? 'You cannot delete your own account' : 'Delete'}>
                      <span>
                        <IconButton size="small" color="error" disabled={isSelf} onClick={() => handleDelete(user)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </td>
                  <td>{[user.first_name, user.last_name].filter(Boolean).join(' ') || '-'}</td>
                  <td>{maskText(user.username) || '-'}{isSelf ? ' (You)' : ''}</td>
                  <td>{maskEmail(user.email) || '-'}</td>
                  <td>{user.role === 'ADMIN' ? 'Admin' : 'User'}</td>
                  <td>{user.access_role_name || '-'}</td>
                  <td>{isActive ? 'Active' : 'Inactive'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(user.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!infoUser} onClose={() => setInfoUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlined color="info" /> User Details
        </DialogTitle>
        <DialogContent dividers>
          {infoUser && (
            <Table size="small">
              <TableBody>
                {[
                  ['Name', [infoUser.first_name, infoUser.last_name].filter(Boolean).join(' ') || '-'],
                  ['Username', infoUser.username || '-'],
                  ['Email', infoUser.email || '-'],
                  ['Role', infoUser.role === 'ADMIN' ? 'Admin' : 'User'],
                  ['Access Role', infoUser.access_role_name || '-'],
                  ['Status', Number(infoUser.is_active) === 1 ? 'Active' : 'Inactive'],
                  ['Created On', formatDate(infoUser.created_at)],
                ].map(([label, value]) => (
                  <TableRow key={label}>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>{label}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>{value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoUser(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Notification notify={notify} setNotify={setNotify} />
    </>
  );
}

export default UsersTable;
